import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import { SITE } from '@/constants/site';

const NAV_ITEMS = [
  { to: '/', key: 'nav.home' },
  { to: '/about', key: 'nav.about' },
  { to: '/processing', key: 'nav.processing' },
  { to: '/contact', key: 'nav.contact' },
  { to: '/imprint', key: 'nav.imprint' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || menuOpen;
  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'border-b border-background-200 bg-background-50/95 backdrop-blur-md' : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-3" aria-label={SITE.name}>
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-background-50 ring-1 ring-background-200 md:h-11 md:w-11">
            <img
              src="https://www.sn-metall.de/mediapool/126/1269216/resources/logo_609_112985789.jpg"
              alt="S+N Blech- und Metallverarbeitungs GmbH"
              className="h-full w-full object-contain p-0.5"
            />
          </span>
          <span className="hidden flex-col leading-tight sm:flex">
            <span className={`font-heading text-base font-bold tracking-tight ${solid ? 'text-foreground-950' : 'text-background-50'}`}>
              S+N Metall
            </span>
            <span className={`text-[11px] uppercase tracking-[0.16em] ${solid ? 'text-foreground-500' : 'text-background-200'}`}>
              Blechverarbeitung
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={`relative rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  solid
                    ? active
                      ? 'text-primary-600'
                      : 'text-foreground-700 hover:text-foreground-950'
                    : active
                      ? 'text-background-50'
                      : 'text-background-200 hover:text-background-50'
                }`}
              >
                {t(item.key)}
                {active && (
                  <span
                    className={`absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full ${solid ? 'bg-primary-500' : 'bg-primary-400'}`}
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <LanguageSwitcher variant={solid ? 'solid' : 'transparent'} />
          </div>
          <Link
            to="/contact"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 md:inline-flex"
          >
            {t('nav.cta')}
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={menuOpen}
            className={`flex h-10 w-10 items-center justify-center rounded-md lg:hidden ${
              solid ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="flex w-full flex-col px-4 py-4 md:px-8">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive: active }) =>
                  `rounded-md px-3 py-3 text-sm font-medium ${
                    active ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-4">
              <LanguageSwitcher variant="solid" />
              <Link
                to="/contact"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
              >
                {t('nav.cta')}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}